import { useEffect, useRef } from 'react';
import { useCharacters } from '@/api';
import { AuthLink } from '@/app/components/AuthLink';
import { MESSAGE_PACKAGES, PAY_CRYPTO_LABEL } from '@/constants/pricing';
import {
  CharactersGrid,
  LoadingState,
  ErrorState,
} from '@/app/components/explore';
import './landing.css';

const STEPS = [
  { num: '01', title: 'Connect Wallet', desc: 'Link your Solana wallet. Your identity, your keys, your conversations — fully sovereign.' },
  { num: '02', title: 'Pick a Character', desc: 'Browse unique AI personalities across Screen Legends, Toons, IRL Vibes, and more.' },
  { num: '03', title: 'Buy Messages', desc: 'Purchase message credits with SOL. Transparent pricing, no hidden subscriptions.' },
  { num: '04', title: 'Start Chatting', desc: 'Dive in. Every message is a unique, context-aware conversation with your chosen character.' },
];

/** Build ticker from platform data */
function getTickerItems(pkgMessages: number[]): string[] {
  const base = ['AI CHARACTERS', `${PAY_CRYPTO_LABEL} PAYMENTS`, 'CHAT NOW', 'WEB3 POWERED'];
  const msgItems = pkgMessages.map((m) => `${m} MESSAGES`);
  return [...base, ...msgItems];
}

export function LandingPage() {
  const gridRef = useRef<HTMLDivElement>(null);
  const feature1Ref = useRef<HTMLDivElement>(null);
  const feature2Ref = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  const { data: charactersData, isLoading: charactersLoading, error: charactersError } = useCharacters({
    limit: 6,
    page: 1,
  });
  const characters = charactersData?.data?.characters ?? [];
  const totalCharacters = charactersData?.data?.pagination?.total ?? 0;

  const tickerItems = getTickerItems(MESSAGE_PACKAGES.map((p) => p.messages));
  const popularPackage = MESSAGE_PACKAGES.find((p) => p.popular) ?? MESSAGE_PACKAGES[1];

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;

      if (gridRef.current) {
        gridRef.current.style.transform = `perspective(800px) rotateX(15deg) scale(1.2) translateY(${y * 0.3}px)`;
      }

      if (feature1Ref.current) {
        feature1Ref.current.style.transform = `translateY(${y * 0.05}px)`;
      }

      if (feature2Ref.current) {
        feature2Ref.current.style.transform = `translateY(${-y * 0.05}px)`;
      }

      document.querySelectorAll<HTMLElement>('.landing-orb').forEach((orb, i) => {
        const speed = [0.15, -0.1, 0.08][i] ?? 0.1;
        orb.style.transform = `translateY(${y * speed}px)`;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const revealEls = document.querySelectorAll('.landing-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.target instanceof HTMLElement) {
            e.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    revealEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const hasAnimatedStats = useRef(false);

  useEffect(() => {
    if (hasAnimatedStats.current) return;

    const animateCounter = (el: HTMLElement | null, target: number) => {
      if (!el) return;
      let current = 0;
      const step = Math.max(target / 60, 0.5);
      const timer = setInterval(() => {
        current += step;
        if (current >= target) {
          el.textContent = String(Math.round(target));
          clearInterval(timer);
        } else {
          el.textContent = String(Math.floor(current));
        }
      }, 20);
    };

    const statsObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !hasAnimatedStats.current) {
            hasAnimatedStats.current = true;
            animateCounter(document.getElementById('landing-counter1'), Math.max(totalCharacters, 1));
            animateCounter(document.getElementById('landing-counter2'), MESSAGE_PACKAGES[MESSAGE_PACKAGES.length - 1]?.messages ?? 150);
            animateCounter(document.getElementById('landing-counter3'), MESSAGE_PACKAGES.length);
            animateCounter(document.getElementById('landing-counter4'), 99);
            statsObs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) statsObs.observe(statsRef.current);
    return () => statsObs.disconnect();
  }, [totalCharacters]);

  return (
    <div className="landing">
      <div className="landing-hero">
        <div className="landing-hero-bg" />
        <div className="landing-grid-overlay landing-parallax-slow" ref={gridRef} />
        <div className="landing-orb landing-orb-1" />
        <div className="landing-orb landing-orb-2" />
        <div className="landing-orb landing-orb-3" />

        <div className="landing-hero-content">
          <div className="landing-eyebrow">✦ Web3-Powered AI Conversations</div>
          <h1 className="landing-title">
            <span className="line1">Talk to</span>
            <span className="line2" data-text="AI Characters">
              AI Characters
            </span>
          </h1>
          <p className="landing-sub">
            Chat with AI personalities. Pay with your wallet. Own your messages.
            The future of AI conversations is decentralized.
          </p>
          <div className="landing-cta">
            <AuthLink to="/explore" className="landing-btn-primary" fallbackLabel="Connect to Start">
              Start Chatting
            </AuthLink>
            <AuthLink to="/explore" className="landing-btn-ghost" fallbackLabel="Connect to Explore">
              Explore Bots
            </AuthLink>
          </div>
        </div>

        <button
          type="button"
          className="landing-scroll-hint"
          onClick={() => statsRef.current?.scrollIntoView({ behavior: 'smooth' })}
          aria-label="Scroll to stats"
        >
          <span>Scroll</span>
          <div className="landing-scroll-line" />
        </button>
      </div>

      <div className="landing-ticker">
        <div className="landing-ticker-track">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <div key={i} className="landing-ticker-item">
              {item} <span className="landing-ticker-dot" />
            </div>
          ))}
        </div>
      </div>

      <div className="landing-stats-band landing-reveal" ref={statsRef}>
        <div className="landing-stat-item">
          <span className="landing-stat-num">
            <span id="landing-counter1">0</span>
            <span className="accent">+</span>
          </span>
          <div className="landing-stat-label">AI Characters</div>
        </div>
        <div className="landing-stat-item">
          <span className="landing-stat-num">
            <span id="landing-counter2">0</span>
          </span>
          <div className="landing-stat-label">Max Messages per Pack</div>
        </div>
        <div className="landing-stat-item">
          <span className="landing-stat-num">
            <span id="landing-counter3">0</span>
          </span>
          <div className="landing-stat-label">Message Packages</div>
        </div>
        <div className="landing-stat-item">
          <span className="landing-stat-num">
            <span id="landing-counter4">0</span>
            <span className="accent">%</span>
          </span>
          <div className="landing-stat-label">Uptime</div>
        </div>
      </div>

      <div className="landing-feature-row">
        <div>
          <div className="landing-section-label landing-reveal">Your Dashboard</div>
          <h2 className="landing-section-title landing-reveal landing-reveal-delay-1">
            Track Every
            <br />
            Conversation
          </h2>
          <p
            className="landing-reveal landing-reveal-delay-2"
            style={{
              fontSize: 16,
              color: 'var(--landing-muted)',
              lineHeight: 1.8,
              maxWidth: 440,
            }}
          >
            Your personal command center. See message credits, active chat threads,
            and your blockchain wallet — all in one sleek dashboard. Real-time.
            Always on.
          </p>
          <div style={{ marginTop: 48 }} className="landing-reveal landing-reveal-delay-3">
            <AuthLink to="/dashboard" className="landing-btn-primary" fallbackLabel="Connect for Dashboard">
              Open Dashboard
            </AuthLink>
          </div>
        </div>
        <div className="landing-feature-visual landing-parallax-fast" ref={feature1Ref}>
          <div className="landing-mock-dashboard">
            <div className="landing-mock-header">
              <div className="landing-mock-dot landing-mock-d1" />
              <div className="landing-mock-dot landing-mock-d2" />
              <div className="landing-mock-dot landing-mock-d3" />
              <div className="landing-mock-title">MY DASHBOARD</div>
            </div>
            <div className="landing-mock-stats">
              <div className="landing-mock-stat">
                <div className="landing-mock-stat-num">{popularPackage.messages}</div>
                <div className="landing-mock-stat-label">Messages</div>
              </div>
              <div className="landing-mock-stat">
                <div className="landing-mock-stat-num">{characters.length || '—'}</div>
                <div className="landing-mock-stat-label">Characters</div>
              </div>
            </div>
            <div className="landing-mock-chat-list">
              {(characters.length > 0 ? characters.slice(0, 4) : [
                { _id: '1', name: 'Explore characters', description: 'Connect wallet to start...', imageUrl: undefined },
              ]).map((c) => (
                <div key={c._id} className="landing-mock-chat-item">
                  <div className="landing-mock-avatar">
                    {'imageUrl' in c && c.imageUrl ? (
                      <img src={c.imageUrl} alt="" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      c.name.charAt(0)
                    )}
                  </div>
                  <div className="landing-mock-info">
                    <div className="landing-mock-name">{c.name}</div>
                    <div className="landing-mock-preview">
                      {(c.description ?? '').slice(0, 28)}
                      {(c.description ?? '').length > 28 ? '...' : ''}
                    </div>
                  </div>
                  <div className="landing-mock-time">—</div>
                </div>
              ))}
            </div>
          </div>
          <div className="landing-glow-card" style={{ top: -15, right: -15 }}>
            <div className="landing-glow-card-label">Messages</div>
            <div className="landing-glow-card-val">{popularPackage.messages}</div>
          </div>
        </div>
      </div>

      <div
        className="landing-section landing-characters-section"
        style={{
          background: '#121212',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <div className="landing-section-label landing-reveal">Meet the Cast</div>
        <h2 className="landing-section-title landing-reveal landing-reveal-delay-1">
          Choose Your
          <br />
          Character
        </h2>
        <p className="landing-reveal landing-reveal-delay-2 text-[#9595a5] mb-8 max-w-xl">
          Discover {totalCharacters > 0 ? totalCharacters : 'blockchain-powered'} AI personalities. Pick one and start chatting.
        </p>

        {charactersLoading ? (
          <div className="mt-8">
            <LoadingState />
          </div>
        ) : charactersError ? (
          <div className="mt-8">
            <ErrorState error={charactersError} />
          </div>
        ) : characters.length > 0 ? (
          <>
            <div className="mt-8">
              <CharactersGrid characters={characters} />
            </div>
            <div className="flex justify-center mt-6">
              <AuthLink
                to="/explore"
                className="landing-btn-primary"
                style={{ fontSize: 14, padding: '14px 32px' }}
                fallbackLabel="Connect to Explore"
              >
                Explore All Characters
              </AuthLink>
            </div>
          </>
        ) : (
          <div className="landing-reveal landing-reveal-delay-3 text-center py-12">
            <p className="text-[#9595a5] mb-6">No characters yet. Connect your wallet to explore when they’re added.</p>
            <AuthLink to="/explore" className="landing-btn-ghost" style={{ fontSize: 14, padding: '14px 32px' }} fallbackLabel="Connect to Explore">
              Go to Explore
            </AuthLink>
          </div>
        )}
      </div>

      <div style={{ background: 'var(--landing-surface)', borderTop: '1px solid var(--landing-border)', borderBottom: '1px solid var(--landing-border)' }}>
        <div className="landing-how-section">
          <div className="landing-section-label landing-reveal">Process</div>
          <h2 className="landing-section-title landing-reveal landing-reveal-delay-1">
            How It Works
          </h2>

          <div className="landing-steps">
            {STEPS.map((step, i) => (
              <div
                key={step.num}
                className={`landing-step landing-reveal ${i > 0 ? 'landing-reveal-delay-' + Math.min(i, 3) : ''}`}
              >
                <div className="landing-step-num">{step.num}</div>
                <div className="landing-step-title">{step.title}</div>
                <p className="landing-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="landing-feature-row reverse">
        <div>
          <div className="landing-section-label landing-reveal">Web3 Native</div>
          <h2 className="landing-section-title landing-reveal landing-reveal-delay-1">
            Your Wallet,
            <br />
            Your Rules
          </h2>
          <p
            className="landing-reveal landing-reveal-delay-2"
            style={{
              fontSize: 16,
              color: 'var(--landing-muted)',
              lineHeight: 1.8,
              maxWidth: 440,
            }}
          >
            Connect Phantom, Solflare, or any Solana wallet. All transactions are transparent,
            on-chain, and non-custodial. ChatZapp never holds your funds — you do.
          </p>
          <div
            style={{ marginTop: 48, display: 'flex', flexDirection: 'column', gap: 16 }}
            className="landing-reveal landing-reveal-delay-3"
          >
            {[
              'Phantom, Solflare, and more',
              'Non-custodial, always',
              'On-chain message credits',
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 14,
                  color: 'var(--landing-muted)',
                }}
              >
                <span style={{ color: 'var(--landing-accent)', fontSize: 18 }}>→</span>
                {item}
              </div>
            ))}
          </div>
        </div>
        <div className="landing-feature-visual landing-parallax-fast" ref={feature2Ref} style={{ direction: 'ltr' }}>
          <div className="landing-mock-dashboard">
            <div className="landing-mock-header">
              <div className="landing-mock-dot landing-mock-d1" />
              <div className="landing-mock-dot landing-mock-d2" />
              <div className="landing-mock-dot landing-mock-d3" />
              <div className="landing-mock-title">WALLET & SETTINGS</div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div
                className="landing-mock-title"
                style={{ marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.1em' }}
              >
                Connected Address
              </div>
              <div
                style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: 12,
                  color: 'var(--landing-accent)',
                  wordBreak: 'break-all',
                  lineHeight: 1.8,
                  background: 'var(--landing-surface2)',
                  padding: 12,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                AWs3T...h2Rjc
              </div>
            </div>
            <div style={{ height: 1, background: 'var(--landing-border)', margin: '20px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--landing-muted)',
                    marginBottom: 4,
                    fontFamily: "'Space Mono', monospace",
                    letterSpacing: '0.05em',
                  }}
                >
                  MESSAGES BALANCE
                </div>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 56, color: 'var(--landing-text)', lineHeight: 1 }}>
                  {popularPackage.messages}
                </div>
              </div>
              <AuthLink
                to="/purchase"
                className="landing-btn-primary"
                style={{ fontSize: 11, padding: '12px 20px' }}
                fallbackLabel="Connect to Buy"
              >
                Buy More
              </AuthLink>
            </div>
            <div style={{ height: 1, background: 'var(--landing-border)', margin: '20px 0' }} />
            <div style={{ display: 'flex', gap: 12 }}>
              <div
                style={{
                  flex: 1,
                  background: 'var(--landing-surface2)',
                  border: '1px solid var(--landing-border)',
                  padding: 12,
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 10, color: 'var(--landing-muted)', marginBottom: 4, fontFamily: "'Space Mono', monospace" }}>
                  CHAIN
                </div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Solana</div>
              </div>
              <div
                style={{
                  flex: 1,
                  background: 'var(--landing-surface2)',
                  border: '1px solid var(--landing-border)',
                  padding: 12,
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 10, color: 'var(--landing-muted)', marginBottom: 4, fontFamily: "'Space Mono', monospace" }}>
                  STATUS
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--landing-accent)' }}>● Active</div>
              </div>
            </div>
          </div>
          <div className="landing-glow-card" style={{ bottom: 40, left: -20, top: 'auto' }}>
            <div className="landing-glow-card-label">Network</div>
            <div className="landing-glow-card-val" style={{ fontSize: 22 }}>
              SOL
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          background: 'var(--landing-surface)',
          borderTop: '1px solid var(--landing-border)',
          borderBottom: '1px solid var(--landing-border)',
        }}
      >
        <div className="landing-pricing-section">
          <div className="landing-section-label landing-reveal">Pricing</div>
          <h2 className="landing-section-title landing-reveal landing-reveal-delay-1">
            Pay Per
            <br />
            Message
          </h2>

          <div className="landing-pricing-grid">
            {MESSAGE_PACKAGES.map((plan, i) => (
              <div
                key={plan.id}
                className={`landing-pricing-card landing-reveal ${i > 0 ? 'landing-reveal-delay-' + Math.min(i, 2) : ''} ${plan.popular ? 'featured' : ''}`}
              >
                {plan.popular ? (
                  <div className="landing-pricing-badge">Most Popular</div>
                ) : (
                  <div style={{ marginBottom: 32 }}>
                    <div
                      style={{
                        fontFamily: "'Space Mono', monospace",
                        fontSize: 10,
                        letterSpacing: '0.3em',
                        color: 'var(--landing-muted)',
                        textTransform: 'uppercase',
                        marginBottom: 8,
                      }}
                    >
                      {plan.messages} messages
                    </div>
                  </div>
                )}
                <div className="landing-pricing-num">
                  <sup>$</sup>
                  {plan.priceUsd < 1 ? plan.priceUsd.toFixed(2) : plan.priceUsd}
                </div>
                <div className="landing-pricing-sub">for {plan.messages} messages</div>
                <ul className="landing-pricing-features">
                  <li>{plan.messages} AI conversations</li>
                  <li>Access to all characters</li>
                  <li>Pay with {PAY_CRYPTO_LABEL}</li>
                  {plan.bonus && <li key="bonus">{plan.bonus}</li>}
                </ul>
                <AuthLink
                  to="/purchase"
                  className={plan.popular ? 'landing-btn-primary' : 'landing-btn-ghost'}
                  style={{ fontSize: 12, padding: '14px 28px' }}
                  fallbackLabel={plan.popular ? 'Connect to Buy' : 'Connect to Get Started'}
                >
                  {plan.popular ? 'Buy Now' : 'Get Started'}
                </AuthLink>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="landing-cta-section">
        <div className="landing-cta-inner">
          <div className="landing-section-label landing-reveal" style={{ justifyContent: 'center' }}>
            Start Now
          </div>
          <h2 className="landing-cta-title landing-reveal landing-reveal-delay-1">
            The Bots
            <br />
            Are <em>Waiting</em>
          </h2>
          <p className="landing-cta-sub landing-reveal landing-reveal-delay-2">
            Connect your wallet. Pick your character. Every message is a new story.
            What will yours be?
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }} className="landing-reveal landing-reveal-delay-3">
            <AuthLink to="/explore" className="landing-btn-primary" style={{ fontSize: 14, padding: '18px 48px' }} fallbackLabel="Connect & Chat">
              Connect & Chat
            </AuthLink>
            <AuthLink to="/voting" className="landing-btn-ghost" style={{ fontSize: 14, padding: '18px 48px' }} fallbackLabel="Connect to Vote">
              Vote on Bots
            </AuthLink>
          </div>
        </div>
      </div>

      <footer className="landing-footer">
        <div className="landing-footer-logo">
          Chat<span>Zapp</span>
        </div>
        <div className="landing-footer-copy">© 2025 ChatZapp. All rights reserved.</div>
        <div className="landing-footer-links">
          <AuthLink to="/explore">Explore</AuthLink>
          <AuthLink to="/voting">Voting</AuthLink>
          <AuthLink to="/dashboard">Dashboard</AuthLink>
        </div>
      </footer>
    </div>
  );
}
